import React from 'react';
import classNames from 'classnames';
import {
  CaptionProps,
  DayPicker,
  Matcher,
  ModifiersClassNames,
  useNavigation,
} from 'react-day-picker';

import { getFormattedDateWithWeekday } from '../../shared/utils/dateHelpers';
import { ReactComponent as ShortArrow } from '../../shared/assets/images/shortArrow.svg';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

import styles from './DatePickerInput.module.scss';

const DatePickerNavBar = (props: CaptionProps) => {
  const { displayMonth } = props;
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const { i18n } = useTranslation();

  return (
    <div className={styles.navBar}>
      <button
        type="button"
        className={classNames(styles.navButton, styles.navButtonPrev)}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          if (previousMonth) goToMonth(previousMonth);
        }}
        disabled={!previousMonth}
      >
        <ShortArrow className={styles.arrowIcon} />
      </button>

      <div className={styles.caption}>
        {format(displayMonth, 'MMMM yyyy')}
      </div>

      <button
        type="button"
        className={classNames(styles.navButton, styles.navButtonNext)}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          if (nextMonth) goToMonth(nextMonth);
        }}
        disabled={!nextMonth}
      >
        <ShortArrow className={styles.arrowIcon} />
      </button>
    </div>
  );
};

const datePickerModifiersClassNames: ModifiersClassNames = {
  container: '',
  wrapper: styles.datePickerWrapper,
  interactionDisabled: '',
  navBar: styles.navBar,
  navButtonPrev: styles.navButtonPrev,
  navButtonNext: styles.navButtonNext,
  navButtonInteractionDisabled: styles.navButtonInteractionDisabled,
  months: styles.months,
  month: styles.month,
  caption: styles.caption,
  weekdays: styles.weekdays,
  weekdaysRow: styles.weekdaysRow,
  weekday: styles.weekday,
  weekNumber: '',
  body: '',
  week: styles.week,
  footer: '',
  disabled: styles.disabled,
  outside: '',
  table: styles.table,
  today: styles.dayToday,
  selected: styles.selected,
  range_middle: styles.rangeMiddleSelection,
};

interface Props {
  text: string;
  selectDate: (date: Date) => void;
  dateToDisplay: Date | undefined;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  disabledDays: Matcher | Matcher[] | undefined;
  theme?: 'dark' | 'light';
  datePickerClassName?: string;
  inputClassName?: string;
  isDatePickerVisible: boolean;
  setDatePickerVisible: (isVisible: boolean) => void;
  inputIconColor?: string;
}

const DatePickerInput = ({
  text,
  selectDate,
  dateToDisplay,
  dateFrom,
  dateTo,
  disabledDays,
  theme,
  datePickerClassName,
  inputClassName,
  isDatePickerVisible,
  setDatePickerVisible,
  inputIconColor,
}: Props) => {
  const { i18n } = useTranslation();

  return (
    <div
      className={classNames(
        styles.container,
        theme === 'dark' && styles.darkTheme,
      )}
      onMouseEnter={() => setDatePickerVisible(true)}
      onMouseLeave={() => setDatePickerVisible(false)}
    >
      <div
        className={classNames(
          styles.input,
          isDatePickerVisible && styles.inputHighlighted,
          inputClassName,
        )}
      >
        <span className={styles.inputText}>{text}</span>

        {dateToDisplay && (
          <span className={styles.dateToDisplay}>
            {getFormattedDateWithWeekday(dateToDisplay, i18n.language)}
          </span>
        )}

        <div className={styles.inputIcon}>
          <ShortArrow
            className={styles.arrowIcon}
            style={{ color: inputIconColor || 'var(--icon-color)' }}
          />
        </div>
      </div>

      <div
        className={classNames(
          isDatePickerVisible && styles.datePickerVisible,
          styles.datePicker,
          datePickerClassName,
        )}
      >
        <DayPicker
          selected={{
            from: dateFrom,
            to: dateTo,
          }}
          disabled={disabledDays}
          classNames={styles}
          components={{
            Caption: DatePickerNavBar,
          }}
          defaultMonth={new Date()}
          className={styles.picker}
          formatters={{
            formatWeekdayName: (date: Date) => format(date, 'EEE'),
          }}
          modifiersClassNames={datePickerModifiersClassNames}
          onDayClick={(day: Date) => selectDate(day)}
          mode="range"
        />
      </div>
    </div>
  );
};

export default DatePickerInput;
